import React from 'react';

import { Badge } from '@/design-system/badge';
import type { MoneyString, UjpEMoney } from '@/services/api/ujp';

import { UJP_COPY } from '../copy';
import { formatRupiah } from '../format';

/**
 * The instrument tiles (CR-D4): Flazz · QRIS · Transfer. QRIS only appears when
 * the UJP actually uses it, so a FLAZZ/NONE request reads exactly as it did
 * before the bucket split. Shared by the panel, the Biaya breakdown and the
 * Review step so all three show the same three numbers.
 */

export const usesQris = (eMoney: UjpEMoney | null | undefined): boolean =>
  eMoney === 'QRIS' || eMoney === 'FLAZZ_QRIS';

export interface UjpTileTotals {
  flazz: MoneyString;
  qris: MoneyString;
  transfer: MoneyString;
}

const TONE = {
  flazz: 'border-(--state-success-base)/25 bg-(--state-success-lighter)',
  qris: 'border-(--state-information-base)/25 bg-(--state-information-lighter)',
  transfer: 'border-(--theme-accent-alpha-16) bg-(--theme-accent-alpha-10)',
} as const;

const Tile: React.FC<{
  label: string;
  amount: MoneyString;
  tone: keyof typeof TONE;
}> = ({ label, amount, tone }) => (
  <div className={`rounded-gsm border px-3 py-2.5 ${TONE[tone]}`}>
    <p className="label-eyebrow mb-1.5">{label}</p>
    <p className="font-mono text-[15px] font-bold tabular-nums text-text-strong-950">
      {formatRupiah(amount)}
    </p>
  </div>
);

export const UjpTiles: React.FC<{
  totals: UjpTileTotals;
  eMoney: UjpEMoney | null | undefined;
  className?: string;
}> = ({ totals, eMoney, className }) => {
  const qris = usesQris(eMoney);
  return (
    <div
      className={`grid gap-2 ${qris ? 'grid-cols-3' : 'grid-cols-2'} ${className ?? ''}`}
    >
      <Tile label={UJP_COPY.estimate.flazz} amount={totals.flazz} tone="flazz" />
      {qris && (
        <Tile
          label={UJP_COPY.estimate.qrisShort}
          amount={totals.qris}
          tone="qris"
        />
      )}
      <Tile
        label={UJP_COPY.estimate.transfer}
        amount={totals.transfer}
        tone="transfer"
      />
    </div>
  );
};

/** "Disembunyikan" stand-in used wherever a masked amount would render. */
export const UjpHiddenAmount: React.FC = () => (
  <Badge status="neutral" size="sm" title={UJP_COPY.panel.maskedTitle}>
    {UJP_COPY.panel.hidden}
  </Badge>
);
